import { Injectable } from '@angular/core';
import { User } from '@app/modules/shared/models/user';

/** Where a user lands when their role has no `default_dashboard`: the Welcome dashboard. */
export const DEFAULT_DASHBOARD_FALLBACK = '/';

@Injectable({
  providedIn: 'root'
})
export class UserAbilityService {
  private currentUser: any = null;

  constructor() {
  }

  private loadUserFromStorage(): void {
    try {
      const userStr = localStorage.getItem('currentUser');
      if (userStr) {
        this.currentUser = JSON.parse(userStr);
      }
    } catch (e) {
      console.error('Failed to load user from localStorage', e);
    }
  }

  setUser(user: any): void {
    this.currentUser = user;
  }

  getUser(): any {
    return this.currentUser;
  }

  /**
   * Landing path after login (or when returning to `/`).
   * `default_dashboard` comes from the login API (`role.default_dashboard`); nothing is
   * hardcoded per role here, so a role without one lands on the Welcome dashboard.
   */
  getDefaultDashboard(user?: User | any): string {
    if (!user && !this.currentUser) {
      this.loadUserFromStorage();
    }
    const resolved = user || this.currentUser;
    return resolved?.default_dashboard || DEFAULT_DASHBOARD_FALLBACK;
  }

  /**
   * Path to open after a successful login.
   * Role default wins over empty/`/` and the legacy global default `/projects`
   * (logout used to stash that as returnUrl). Deep links are still honored.
   */
  resolvePostLoginUrl(user: User | any, returnUrl?: string): string {
    const roleHome = this.getDefaultDashboard(user);
    const raw = (returnUrl || '').trim();
    if (!raw) {
      return roleHome;
    }
    let path: string;
    try {
      path = decodeURI(raw).split('?')[0] || '';
    } catch {
      path = raw.split('?')[0] || '';
    }
    const normalized = !path || path.startsWith('/') ? path : `/${path}`;
    if (!normalized || normalized === '/' || normalized === '/projects') {
      return roleHome;
    }
    return normalized;
  }

  /**
   * Whether the user holds the server's rule `<resource>.<action>`. Every login-shaped response
   * carries the names the user holds (`abilities`, Ability.js panelAbilitiesFor); the panel keeps
   * no copy of who holds what.
   */
  can(action: string, resource: string): boolean {
    if (!this.currentUser) {
      this.loadUserFromStorage();
    }
    return !!this.currentUser?.abilities?.includes(`${resource}.${action}`);
  }

  /** Admins, and an organization's own manager: who edits the organization fields of its settings. */
  canManageOrganizationFields(): boolean {
    return this.can('index.all', 'organization') || this.can('manage', 'client');
  }

  /** Configuring this organization's single sign-on: any organization's, or the user's own. */
  canManageOrganizationSso(organizationId: number | string | null | undefined): boolean {
    if (this.can('manage-organization-sso', 'navigation')) {
      return true;
    }
    return this.can('manage-own-organization-sso', 'navigation') && organizationId != null
      && Number(organizationId) === Number(this.currentUser?.organization_id);
  }
}
